import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getAccountTrades } from '../../api/api';
import PerformanceStats from '../PerformanceStats';
import SpecificTradeDetails from '../SpecificTradeDetails';
import { Modal } from '../ui/modal';

// Contract
// Inputs:
// - accountId?: string (fetch trades if provided and no trades prop)
// - trades?: array (use directly if provided)
// - initialStartDate?: string (YYYY-MM-DD)
// - initialEndDate?: string (YYYY-MM-DD)
// - defaultVisibleSections?: { filters?: boolean, metrics?: boolean, trades?: boolean }
// - initialColumns?: string[] keys from AVAILABLE_COLUMNS
// - onColumnsChange?: (cols: string[]) => void
// - compact?: boolean
// Outputs: renders filters, performance metrics (hideable), and a trades table with selectable columns

const COMPACT_VISIBLE_ROWS = 8;
const COMPACT_FILTERS = [
  { key: 'last7Days', label: '7D' },
  { key: 'last30Days', label: '30D' },
  { key: 'last90Days', label: '90D' },
];
const COMPACT_BREAKPOINTS = {
  narrow: 520,
  medium: 780,
};
const COMPACT_SUMMARY_BREAKPOINTS = {
  minimal: 360,
};

const AVAILABLE_COLUMNS = [
  {
    key: 'instrument',
    label: 'Instrument',
    sortType: 'string',
    getValue: (t) => String(t.instrument ?? '').toLowerCase(),
    render: (t) => t.instrument,
  },
  {
    key: 'direction',
    label: 'Direction',
    sortType: 'string',
    getValue: (t) => String(t.direction ?? '').toLowerCase(),
    render: (t) => (
      <span className={t.direction === 'BUY' ? 'text-success' : 'text-destructive'}>{t.direction}</span>
    ),
  },
  {
    key: 'entry_price',
    label: 'Entry',
    sortType: 'number',
    getValue: (t) => parseFloat(t.entry_price),
    render: (t) => t.entry_price,
  },
  {
    key: 'lot_size',
    label: 'Lots',
    sortType: 'number',
    getValue: (t) => parseFloat(t.lot_size),
    render: (t) => t.lot_size,
  },
  {
    key: 'trade_status',
    label: 'Status',
    sortType: 'string',
    getValue: (t) => String(t.trade_status ?? '').toLowerCase(),
    render: (t) => (
      <span className={`font-semibold ${t.trade_status === 'closed' ? 'text-textSecondary' : 'text-yellow-500 dark:text-yellow-400'}`}>{t.trade_status}</span>
    ),
  },
  {
    key: 'actual_profit_loss',
    label: 'P/L',
    sortType: 'number',
    getValue: (t) => parseFloat(t.actual_profit_loss),
    render: (t) => (
      <span className={getProfitLossColorClass(t.actual_profit_loss)}>{fmtNum(t.actual_profit_loss)}</span>
    ),
  },
  {
    key: 'max_drawdown',
    label: 'DD',
    sortType: 'number',
    getValue: (t) => parseFloat(t.max_drawdown),
    render: (t) => fmtNum(t.max_drawdown),
  },
  {
    key: 'max_runup',
    label: 'RU',
    sortType: 'number',
    getValue: (t) => parseFloat(t.max_runup),
    render: (t) => fmtNum(t.max_runup),
  },
  {
    key: 'close_reason',
    label: 'Close Reason',
    sortType: 'string',
    getValue: (t) => String(t.close_reason ?? '').toLowerCase(),
    render: (t) => t.close_reason,
  },
  {
    key: 'source',
    label: 'Source',
    sortType: 'string',
    getValue: (t) => String(t.source ?? '').toLowerCase(),
    render: (t) => t.source,
  },
  {
    key: 'created_at',
    label: 'Created',
    sortType: 'date',
    getValue: (t) => new Date(t.created_at).getTime(),
    render: (t) => formatDate(t.created_at),
  },
];

const DEFAULT_COLUMNS = [
  'instrument',
  'direction',
  'entry_price',
  'lot_size',
  'trade_status',
  'actual_profit_loss',
  'max_drawdown',
  'max_runup',
  'close_reason',
  'source',
  'created_at',
];

function fmtNum(v) {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return 'N/A';
  return n.toFixed(2);
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return String(dateString);
  }
}

function formatCompactDate(dateString) {
  if (!dateString) return 'N/A';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  } catch {
    return formatDate(dateString);
  }
}

function getProfitLossColorClass(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return 'text-textSecondary';
  if (n > 0) return 'text-success';
  if (n < 0) return 'text-destructive';
  return 'text-textSecondary';
}

const TradeHistoryWidget = ({
  accountId,
  trades: tradesProp,
  initialStartDate,
  initialEndDate,
  defaultVisibleSections = { filters: true, metrics: true, trades: true },
  initialColumns,
  onColumnsChange,
  className = '',
  hideTitle = false,
  compact = false,
}) => {
  const [trades, setTrades] = useState(tradesProp || []);
  const [loading, setLoading] = useState(!tradesProp && !!accountId);
  const [error, setError] = useState(null);
  const [activeTrade, setActiveTrade] = useState(null);
  const [compactSurfaceWidth, setCompactSurfaceWidth] = useState(0);
  const compactRootRef = useRef(null);

  // Sections visibility
  const [showFilters, setShowFilters] = useState(defaultVisibleSections.filters !== false);
  const [showMetrics, setShowMetrics] = useState(defaultVisibleSections.metrics !== false);
  const [showTrades, setShowTrades] = useState(defaultVisibleSections.trades !== false);

  // Dates
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d;
  }, []);
  const [startDate, setStartDate] = useState(
    initialStartDate || defaultStart.toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    initialEndDate || today.toISOString().split('T')[0]
  );
  const [activeDateFilter, setActiveDateFilter] = useState('last30Days');

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Columns
  const storageKey = accountId ? `trade-history-widget.columns.${accountId}` : 'trade-history-widget.columns';
  const widthsStorageKey = accountId ? `trade-history-widget.widths.${accountId}` : 'trade-history-widget.widths';
  const [columns, setColumns] = useState(() => {
    if (initialColumns && initialColumns.length) return initialColumns;
    try {
      const persisted = localStorage.getItem(storageKey);
      if (persisted) {
        const parsed = JSON.parse(persisted);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
  } catch {
      // ignore
    }
    return DEFAULT_COLUMNS;
  });
  const [columnsPanelOpen, setColumnsPanelOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(widthsStorageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });

  // Fetch trades if needed
  useEffect(() => {
    if (tradesProp) {
      setTrades(tradesProp);
      setLoading(false);
      setError(null);
      return;
    }
    if (!accountId) return; // nothing to fetch
    setLoading(true);
    setError(null);
    const params = { page_size: 100 };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    getAccountTrades(accountId, params)
      .then((data) => {
        // API returns paginated response: { count, next, previous, results }
        const tradesArray = data?.results || data;
        setTrades(Array.isArray(tradesArray) ? tradesArray : []);
      })
      .catch((err) => setError(err.message || 'Failed to fetch trades.'))
      .finally(() => setLoading(false));
  }, [accountId, tradesProp, startDate, endDate]);

  // Persist columns selection
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columns));
  } catch {
      // ignore
    }
    if (onColumnsChange) onColumnsChange(columns);
  }, [columns, storageKey, onColumnsChange]);

  // Persist column widths
  useEffect(() => {
    try {
      localStorage.setItem(widthsStorageKey, JSON.stringify(columnWidths));
    } catch {
      // ignore
    }
  }, [columnWidths, widthsStorageKey]);

  // Column resize handlers
  const resizeStateRef = useRef({ activeKey: null, startX: 0, startW: 0 });
  const onResizeMove = useCallback((event) => {
    const { activeKey, startX, startW } = resizeStateRef.current;
    if (!activeKey) return;
    const delta = event.clientX - startX;
    const next = Math.max(60, Math.round(startW + delta));
    setColumnWidths((prev) => ({ ...prev, [activeKey]: next }));
  }, []);
  const onResizeEnd = useCallback(() => {
    resizeStateRef.current = { activeKey: null, startX: 0, startW: 0 };
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', onResizeEnd);
  }, [onResizeMove]);
  const onStartResize = (key, event) => {
    const th = event.currentTarget.parentElement; // resizer lives in <th>
    const rect = th.getBoundingClientRect();
    resizeStateRef.current = { activeKey: key, startX: event.clientX, startW: rect.width };
    document.addEventListener('mousemove', onResizeMove);
    document.addEventListener('mouseup', onResizeEnd);
  };
  useEffect(() => () => {
    // Cleanup listeners on unmount if any
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', onResizeEnd);
  }, [onResizeMove, onResizeEnd]);

  useEffect(() => {
    if (!compact) return undefined;

    const root = compactRootRef.current;
    if (!root) return undefined;

    const measure = () => {
      setCompactSurfaceWidth(root.clientWidth || 0);
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure());
      observer.observe(root);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    return undefined;
  }, [compact]);

  const handleSetDateFilter = (filter) => {
    setActiveDateFilter(filter);
    const now = new Date();
    if (filter === 'today') {
      const s = now.toISOString().split('T')[0];
      setStartDate(s);
      setEndDate(s);
    } else if (filter === 'thisWeek') {
      const startOfWeek = new Date(now);
      const day = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - day);
      setStartDate(startOfWeek.toISOString().split('T')[0]);
      setEndDate(new Date().toISOString().split('T')[0]);
    } else if (filter === 'last30Days') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    } else if (filter === 'last7Days' || filter === 'last90Days') {
      const rangeDays = filter === 'last7Days' ? 7 : 90;
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - (rangeDays - 1));
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  const filteredTrades = useMemo(() => {
    if (!startDate || !endDate) return trades;
    const s = new Date(startDate);
    s.setHours(0, 0, 0, 0);
    const e = new Date(endDate);
    e.setHours(23, 59, 59, 999);
    return (trades || []).filter((t) => {
      const d = new Date(t.created_at);
      return d >= s && d <= e;
    });
  }, [trades, startDate, endDate]);

  const sortedTrades = useMemo(() => {
    if (!filteredTrades || filteredTrades.length === 0) return filteredTrades;
    const col = AVAILABLE_COLUMNS.find((c) => c.key === sortConfig.key);
    if (!col) return filteredTrades;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const getVal = col.getValue || ((t) => t[col.key]);
    const sortType = col.sortType || 'string';
    const normalize = (v) => {
      if (sortType === 'number') {
        const n = parseFloat(v);
        return Number.isNaN(n) ? -Infinity : n;
      }
      if (sortType === 'date') {
        const n = Number(v);
        return Number.isNaN(n) ? -Infinity : n;
      }
      return String(v ?? '').toLowerCase();
    };
    const arr = [...filteredTrades];
    arr.sort((a, b) => {
      const av = normalize(getVal(a));
      const bv = normalize(getVal(b));
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filteredTrades, sortConfig]);

  const totalPL = useMemo(() => {
    return filteredTrades.reduce((acc, t) => acc + (parseFloat(t.actual_profit_loss) || 0), 0);
  }, [filteredTrades]);

  const tradeCount = filteredTrades.length;

  const winningTradeCount = useMemo(() => {
    return filteredTrades.filter((trade) => (parseFloat(trade.actual_profit_loss) || 0) > 0).length;
  }, [filteredTrades]);

  const winRate = useMemo(() => {
    if (tradeCount === 0) return 0;
    return (winningTradeCount / tradeCount) * 100;
  }, [tradeCount, winningTradeCount]);

  const averagePL = useMemo(() => {
    if (tradeCount === 0) return 0;
    return totalPL / tradeCount;
  }, [totalPL, tradeCount]);

  const visibleColumnDefs = useMemo(() => {
    const set = new Set(columns);
    return AVAILABLE_COLUMNS.filter((c) => set.has(c.key));
  }, [columns]);

  const compactDensity = useMemo(() => {
    if (compactSurfaceWidth > 0 && compactSurfaceWidth < COMPACT_BREAKPOINTS.narrow) {
      return 'narrow';
    }
    if (compactSurfaceWidth > 0 && compactSurfaceWidth < COMPACT_BREAKPOINTS.medium) {
      return 'medium';
    }
    return 'wide';
  }, [compactSurfaceWidth]);

  const compactVisibleRows = useMemo(() => {
    if (compactDensity === 'narrow') return 5;
    if (compactDensity === 'medium') return 6;
    return COMPACT_VISIBLE_ROWS;
  }, [compactDensity]);

  const compactSummaryCards = useMemo(() => {
    const cards = [
      {
        key: 'netPl',
        label: 'Net P/L',
        value: fmtNum(totalPL),
        valueClassName: getProfitLossColorClass(totalPL),
      },
      {
        key: 'trades',
        label: 'Trades',
        value: String(tradeCount),
        valueClassName: 'text-textPrimary',
      },
      {
        key: 'winRate',
        label: 'Win Rate',
        value: tradeCount > 0 ? `${winRate.toFixed(0)}%` : '0%',
        valueClassName: 'text-textPrimary',
      },
      {
        key: 'avgPl',
        label: 'Avg P/L',
        value: fmtNum(averagePL),
        valueClassName: getProfitLossColorClass(averagePL),
      },
    ];

    if (compactSurfaceWidth > 0 && compactSurfaceWidth < COMPACT_SUMMARY_BREAKPOINTS.minimal) {
      return cards.filter((card) => card.key === 'netPl' || card.key === 'trades');
    }
    return cards;
  }, [averagePL, compactSurfaceWidth, totalPL, tradeCount, winRate]);

  const compactSummaryGridClassName = compactSummaryCards.length === 2
    ? 'grid-cols-2'
    : 'grid-cols-2 xl:grid-cols-4';

  const showCompactLotsColumn = compactDensity !== 'narrow';
  const showCompactEntryMeta = compactDensity === 'wide';
  const compactGridClassName = showCompactLotsColumn
    ? 'grid-cols-[minmax(0,1fr)_4.5rem_5.25rem]'
    : 'grid-cols-[minmax(0,1fr)_5.25rem]';

  const compactTrades = useMemo(() => {
    return sortedTrades.slice(0, compactVisibleRows);
  }, [compactVisibleRows, sortedTrades]);

  const viewAllHref = accountId ? `/accounts/${accountId}/trade-history` : '/history';
  const activeTradeDetailHref = activeTrade?.id ? `/trades/history/${activeTrade.id}` : viewAllHref;

  const toggleColumn = (key) => {
    setColumns((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  };

  if (compact) {
    return (
      <div ref={compactRootRef} className={`flex h-full min-h-0 flex-col gap-3 ${className}`}>
        <div className="flex flex-wrap items-center gap-2">
          {!hideTitle && <h2 className="text-base font-semibold text-textPrimary">Trade History</h2>}
          {hideTitle && (
            <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">
              {tradeCount} trades
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {COMPACT_FILTERS.map((filter) => {
              const isActive = activeDateFilter === filter.key;
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => handleSetDateFilter(filter.key)}
                  className={`inline-flex h-8 items-center rounded-md border px-2.5 text-[11px] font-medium transition ${
                    isActive
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'widget-muted-border bg-backgroundSurface/30 text-textSecondary hover:bg-secondary/10 hover:text-textPrimary'
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
            <Link
              to={viewAllHref}
              className="inline-flex h-8 items-center rounded-md border widget-muted-border bg-backgroundSurface/30 px-2.5 text-[11px] font-medium text-primary transition hover:border-primary hover:bg-primary/10 hover:text-primaryHover"
            >
              View all
            </Link>
          </div>
        </div>

        <div className={`grid shrink-0 gap-2 ${compactSummaryGridClassName}`}>
          {compactSummaryCards.map((card) => (
            <div key={card.key} className="rounded-xl border widget-muted-border bg-backgroundSurface/20 p-2.5">
              <div className="text-[10px] font-medium uppercase tracking-[0.06em] text-textSecondary">{card.label}</div>
              <div className={`mt-1 text-sm font-semibold ${card.valueClassName}`}>{card.value}</div>
            </div>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border widget-muted-border bg-backgroundSurface/20">
          <div className={`grid ${compactGridClassName} gap-3 border-b widget-divider-border widget-header-fill px-3 py-2 text-[10px] font-medium uppercase tracking-[0.06em] text-textSecondary`}>
            <span>Trade</span>
            {showCompactLotsColumn && <span className="text-right">Lots</span>}
            <span className="text-right">P/L</span>
          </div>

          {loading && (
            <div className="flex flex-1 items-center justify-center px-4 text-sm text-textSecondary">
              Loading trades...
            </div>
          )}

          {!loading && error && (
            <div className="m-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {!loading && !error && compactTrades.length === 0 && (
            <div className="flex flex-1 items-center justify-center px-4 text-center text-sm text-textSecondary">
              No trades in the selected period.
            </div>
          )}

          {!loading && !error && compactTrades.length > 0 && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {compactTrades.map((trade, index) => (
                  <button
                    type="button"
                    onClick={() => {
                      if (trade.id) {
                        setActiveTrade(trade);
                      }
                    }}
                    key={trade.id ?? `${trade.instrument ?? 'trade'}-${trade.created_at ?? index}-${index}`}
                    className={`grid w-full ${compactGridClassName} gap-3 px-3 ${compactDensity === 'narrow' ? 'py-2' : 'py-2.5'} text-left text-xs transition hover:bg-secondary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                      trade.id ? 'cursor-pointer' : 'cursor-default opacity-80'} ${
                      index < compactTrades.length - 1 ? 'border-b widget-divider-border' : ''
                    }`}
                    title={trade.id ? 'Open trade details' : 'Trade details unavailable'}
                    disabled={!trade.id}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-textPrimary">{trade.instrument || 'Unknown'}</span>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                            trade.direction === 'BUY'
                              ? 'border-success/25 bg-success/10 text-success'
                              : 'border-destructive/25 bg-destructive/10 text-destructive'
                          }`}
                        >
                          {trade.direction || '-'}
                        </span>
                      </div>
                      <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-textSecondary ${compactDensity === 'narrow' ? 'text-[10px]' : 'text-[11px]'}`}>
                        <span>{formatCompactDate(trade.created_at)}</span>
                        {showCompactEntryMeta && <span className="font-mono">Entry {fmtNum(trade.entry_price)}</span>}
                      </div>
                    </div>
                    {showCompactLotsColumn && <div className="text-right font-mono text-textPrimary">{fmtNum(trade.lot_size)}</div>}
                    <div className={`text-right font-mono font-semibold ${getProfitLossColorClass(trade.actual_profit_loss)}`}>
                      {fmtNum(trade.actual_profit_loss)}
                    </div>
                  </button>
                ))}
              </div>

              {tradeCount > compactTrades.length && (
                <div className="border-t widget-divider-border px-3 py-2 text-[11px] text-textSecondary">
                  Showing {compactTrades.length} of {tradeCount} trades
                </div>
              )}
            </>
          )}
        </div>

        <Modal
          open={Boolean(activeTrade)}
          onClose={() => setActiveTrade(null)}
          title={activeTrade?.instrument ? `Trade Details - ${activeTrade.instrument}` : 'Trade Details'}
          maxWidthClass="max-w-5xl"
          contentClassName="max-h-[78vh] overflow-auto pr-1"
          footer={
            activeTrade ? (
              <Link
                to={activeTradeDetailHref}
                className="inline-flex items-center rounded-md border border-primary/25 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary transition hover:bg-primary/15 hover:text-primaryHover"
              >
                Open Full Page
              </Link>
            ) : null
          }
        >
          {activeTrade && <SpecificTradeDetails initialTradeData={activeTrade} />}
        </Modal>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg bg-backgroundSurface ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        {!hideTitle && <h2 className="text-textPrimary text-lg font-semibold">Trade History</h2>}
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="px-3 py-1.5 text-sm rounded-md bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            type="button"
            onClick={() => setShowMetrics((v) => !v)}
            className="px-3 py-1.5 text-sm rounded-md bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary"
          >
            {showMetrics ? 'Hide Metrics' : 'Show Metrics'}
          </button>
          <button
            type="button"
            onClick={() => setShowTrades((v) => !v)}
            className="px-3 py-1.5 text-sm rounded-md bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary"
          >
            {showTrades ? 'Hide Trades' : 'Show Trades'}
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setColumnsPanelOpen((v) => !v)}
              className="px-3 py-1.5 text-sm rounded-md bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary"
            >
              Columns
            </button>
            {columnsPanelOpen && (
              <div className="absolute right-0 z-20 mt-2 w-64 max-h-80 overflow-auto rounded-md border border-borderDefault bg-backgroundSurface shadow-lg p-2">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-textSecondary">Select Columns</span>
                  <button
                    type="button"
                    className="text-xs text-primary"
                    onClick={() => setColumns(DEFAULT_COLUMNS)}
                    title="Reset to default"
                  >
                    Reset
                  </button>
                </div>
                <ul className="space-y-1">
                  {AVAILABLE_COLUMNS.map((c) => (
                    <li key={c.key} className="flex items-center gap-2">
                      <input
                        id={`col-${c.key}`}
                        type="checkbox"
                        className="h-4 w-4"
                        checked={columns.includes(c.key)}
                        onChange={() => toggleColumn(c.key)}
                      />
                      <label htmlFor={`col-${c.key}`} className="text-sm text-textPrimary cursor-pointer">
                        {c.label}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 p-3 bg-backgroundSurface border border-borderDefault rounded-lg flex flex-col md:flex-row md:items-center md:justify-start gap-3 md:gap-4">
          <div className="flex items-center gap-2 border border-borderDefault rounded-md p-2 bg-backgroundBody">
            <span className="text-lg text-textSecondary">📅</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActiveDateFilter(null);
              }}
              className="bg-transparent text-textPrimary text-sm focus:outline-none"
            />
            <span className="text-textSecondary">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActiveDateFilter(null);
              }}
              className="bg-transparent text-textPrimary text-sm focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSetDateFilter('today')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeDateFilter === 'today' ? 'bg-primary text-textAccent font-semibold' : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
            >
              Today
            </button>
            <button
              onClick={() => handleSetDateFilter('thisWeek')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeDateFilter === 'thisWeek' ? 'bg-primary text-textAccent font-semibold' : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
            >
              This Week
            </button>
            <button
              onClick={() => handleSetDateFilter('last30Days')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeDateFilter === 'last30Days' ? 'bg-primary text-textAccent font-semibold' : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
            >
              Last 30 Days
            </button>
          </div>

          <div className="ml-auto flex items-center gap-2 p-2 rounded-md bg-backgroundBody">
            <span className="text-sm font-medium text-textSecondary">Total P/L:</span>
            <span className={`text-lg font-bold ${getProfitLossColorClass(totalPL)}`}>{totalPL.toFixed(2)}</span>
          </div>
        </div>
      )}

      {loading && <p className="text-textSecondary">Loading trades…</p>}
      {error && <p className="text-destructive">{error}</p>}

      {showMetrics && !loading && !error && (
        <PerformanceStats trades={filteredTrades} />
      )}

      {showTrades && !loading && !error && (
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          {sortedTrades.length === 0 ? (
            <p className="text-textSecondary">No trades for the selected period.</p>
          ) : (
            <table className="min-w-full text-sm bg-backgroundSurface rounded border border-borderDefault table-fixed">
              <colgroup>
                {visibleColumnDefs.map((c) => (
                  <col key={c.key} style={{ width: columnWidths[c.key] ? `${columnWidths[c.key]}px` : undefined }} />
                ))}
              </colgroup>
              <thead className="bg-slate-50 dark:bg-slate-700">
                <tr>
                  {visibleColumnDefs.map((c) => {
                    const isSorted = sortConfig.key === c.key;
                    const ariaSort = isSorted ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none';
                    const canSort = true; // all columns sortable for now
                    const onClick = () => {
                      setSortConfig((prev) => {
                        if (prev.key === c.key) {
                          return { key: c.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
                        }
                        // Default dir: strings asc, numbers/dates desc
                        const defDir = (c.sortType === 'number' || c.sortType === 'date') ? 'desc' : 'asc';
                        return { key: c.key, direction: defDir };
                      });
                    };
                    return (
                      <th key={c.key} aria-sort={ariaSort} className="p-0 text-left whitespace-nowrap select-none relative sticky top-0 z-10 bg-slate-50 dark:bg-slate-700">
                        <button
                          type="button"
                          onClick={canSort ? onClick : undefined}
                          className="w-full px-2 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-600 flex items-center gap-1"
                          title={`Sort by ${c.label}`}
                        >
                          <span className="font-medium">{c.label}</span>
                          {isSorted && (
                            <span className="text-xs opacity-70">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                          )}
                        </button>
                        <span
                          onMouseDown={(e) => onStartResize(c.key, e)}
                          className="absolute top-0 right-0 h-full w-1 cursor-col-resize select-none z-10 hover:bg-slate-300/40 dark:hover:bg-slate-600/40"
                          title="Drag to resize"
                          role="separator"
                          aria-orientation="vertical"
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedTrades.map((t) => (
                  <tr key={t.id} className="border-b border-borderDefault hover:bg-slate-50 dark:hover:bg-slate-700">
                    {visibleColumnDefs.map((c) => (
                      <td key={c.key} className="p-2 align-top">{c.render(t)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

TradeHistoryWidget.propTypes = {
  accountId: PropTypes.string,
  trades: PropTypes.array,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string,
  defaultVisibleSections: PropTypes.shape({
    filters: PropTypes.bool,
    metrics: PropTypes.bool,
    trades: PropTypes.bool,
  }),
  initialColumns: PropTypes.arrayOf(PropTypes.string),
  onColumnsChange: PropTypes.func,
  className: PropTypes.string,
  hideTitle: PropTypes.bool,
  compact: PropTypes.bool,
};

export default TradeHistoryWidget;
